import type { MetricCatalogEntry, WidgetConfig } from '@contracts';
import { METRIC_CATALOG } from '@contracts';
import type { SourceConfig } from '@/services/sourceConfigService';

/**
 * Las 3 empresas del sistema (slug→nombre por defecto). Lista canónica para el ⚙ de fuentes:
 * incluye SmartBamboo aunque el admin no la tenga en su JWT. El nombre real, si el usuario sí
 * la tiene, lo pisa `getCompanies()`; esto es el fallback. (Mismos slugs que filterContext.)
 */
export const ALL_COMPANIES: ReadonlyArray<{ slug: string; name: string }> = [
  { slug: 'vg', name: 'Vine Garden' },
  { slug: 'pi', name: 'Piélé' },
  { slug: 'sb', name: 'Smart Bamboo' },
];

/**
 * Membresía DERIVADA de una fuente → compañías (slugs). Odoo = VG+Piélé COMPARTEN;
 * SmartBamboo = su propio set. Es el default; el admin la edita desde el ⚙ (overrides).
 */
export function companiesForMetric(entry: MetricCatalogEntry): string[] {
  const out: string[] = [];
  if (entry.sources.includes('odoo')) out.push('vg', 'pi');
  if (entry.sources.includes('sb')) out.push('sb');
  return out;
}

/**
 * ¿La fuente/métrica aplica a las compañías ACTIVAS? Usa el override del admin si existe,
 * si no, la membresía derivada. Si solo estás en VG+Piélé, las exclusivas de SB no aparecen.
 * Misma regla para el catálogo (agregar widget) y los widgets ya puestos (DRY).
 */
export function metricAppliesToCompanies(
  entry: MetricCatalogEntry | null,
  companies: string[],
  overrides?: SourceConfig,
): boolean {
  if (!companies.length || !entry) return true; // sin filtro o sin métrica → visible
  const override = overrides?.[entry.key];
  // Override del admin: se respeta TAL CUAL (desmarcar todo = no pertenece a ninguna → oculto).
  if (override) return override.some((c) => companies.includes(c));
  // Sin override: membresía derivada (si por algo quedara vacía, no restringe).
  const member = companiesForMetric(entry);
  return member.length === 0 || member.some((c) => companies.includes(c));
}

/** Total Ventas por-empresa (comparativos): métrica → su compañía. */
export const PER_COMPANY_METRIC: Record<string, string> = { salesVg: 'vg', salesPi: 'pi', salesSb: 'sb' };

export function widgetAppliesToCompanies(
  config: WidgetConfig,
  companies: string[],
  overrides?: SourceConfig,
): boolean {
  // Custom pinneado a compañías (ej. Apartados VG): visible SOLO si alguna está activa
  // (filtras Pielé → Apartados VG se oculta). Sin pin → sigue el filtro global, siempre visible.
  if (config.customQuery) {
    const pin = config.customQuery.companies;
    if (pin?.length) return companies.length === 0 || pin.some((c) => companies.includes(c));
    return true;
  }
  if (!config.metric) return true;
  // Comparativo por-empresa: visible SOLO si su compañía está activa Y hay >1 empresa
  // en el filtro (si filtras a una sola empresa, se ocultan — ya no hay qué comparar).
  const co = PER_COMPANY_METRIC[config.metric];
  if (co) {
    const multi = companies.length === 0 || companies.length > 1;
    const active = companies.length === 0 || companies.includes(co);
    return multi && active;
  }
  const entry = METRIC_CATALOG.find((m) => m.key === config.metric) ?? null;
  return metricAppliesToCompanies(entry, companies, overrides);
}
